import Input from './Input';
import Sprite from './Sprite';
import Camera from './Camera';
import TileMap from './TileMap';
import Bubbles, { wrap } from './Bubbles';
import Floaters from './Floaters';
import { coinRects } from './coinSprite';
import { variantFor } from './SpriteVariants';
import SnapshotBuffer from './SnapshotBuffer';
import { findPath, steer, reached } from './pathfind';
import { zoneAt } from './zoneShapes';
import { zoneLabelFor } from './closedLabel';
import {
  VIEW_WIDTH, VIEW_HEIGHT, VIEW_SCALE, PLAYER_SIZE, FACINGS, FOOT_OFFSET_Y,
  TALK_RANGE, CLAIMABLE_KINDS, SPRITE_SIZE,
} from './events';

const PUBLIC = process.env.PUBLIC_URL || '';
/**
 * The fallback sheet, drawn for a player whose appearance has not arrived yet.
 *
 * Per-player characters come from recoloured copies of the geometry variants
 * (see SpriteVariants). This is only what shows in the gap between a player
 * appearing in a snapshot and their roster entry landing.
 */
const HERO_SHEET = `${PUBLIC}/sprites/hero-slim-short.png`;

/**
 * Per-player animation offset in ms, so a group walking together does not
 * march in visible lockstep.
 *
 * Derived from the short id rather than stored, which matters because short
 * ids are recycled — a cached phase would follow the id to its next owner.
 * 137 is coprime with 400, so consecutive ids land far apart in the cycle;
 * hashing the id as a string would put "1", "2" and "3" within 2ms of each
 * other and defeat the entire purpose.
 */
const phaseFor = (shortId) => (shortId * 137) % 400;

/**
 * How long to keep trying before abandoning a clicked walk.
 *
 * Time, not frames — see `followPath`.
 */
const STALL_GIVE_UP_MS = 1500;

/** Drawn for a player whose roster entry has not arrived yet. */
const UNKNOWN_COLOR = '#8899AA';

/**
 * How close the local player must be for a sign's text to appear, in world
 * units. Roughly a third of the viewport width — near enough that it reads as
 * standing at the sign, far enough that you do not have to hunt for the spot.
 */
const SIGN_READ_RANGE = 56;

/**
 * Ceiling on the text layer's pixel density.
 *
 * Past 2x nobody can see the difference, but the backing store grows with the
 * square — a 3x display would otherwise allocate 2880x1620 and clear it sixty
 * times a second for no visible gain.
 */
const MAX_TEXT_DPR = 2;

/**
 * The client-side engine.
 *
 * Owns the canvas, the render loop, and the last snapshot received. Knows
 * nothing about React, sockets, or authentication — it is handed snapshots and
 * asked for input. That isolation is the whole point of the boundary: this
 * file can be rewritten freely without touching a component.
 *
 * TWO SOURCES OF TRUTH, deliberately. Remote players are drawn from
 * SnapshotBuffer's interpolated view, 100ms in the past, which is what removes
 * the 20Hz stepping. The local player and the camera use the newest position
 * with no delay, because lagging your own character behind your own keystrokes
 * is exactly what the delay was supposed to avoid.
 *
 * NOTE ON WHAT THIS DOES NOT DO YET:
 *  - No prediction. The local player only moves after a server round trip. On
 *    localhost that is ~1ms and feels fine, which is exactly what makes it
 *    misleading — on a real network it would feel broken.
 */

/** Shown outside the map, on the axis where a small map cannot fill the view. */
const BG = '#0D1B2A';

/** Cull margin, in world units — a sprite half-visible at the edge still draws. */
const SPRITE_MARGIN = 48;

/** The same idea for coins, which are 7px rather than 32 and need far less. */
const COIN_MARGIN = 8;

export default class GameEngine {
  constructor(canvas, { onInputChange, onTalk, textCanvas } = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.input = new Input();
    this.onInputChange = onInputChange || (() => {});
    /**
     * "Talk to whoever I am next to" — the same message the E key sends.
     *
     * A callback rather than a socket, for the reason the whole file is built
     * around: this module knows nothing about sockets or React. Clicking
     * somebody is a new way to send a message that already exists, not a new
     * message — the server still decides who is in range, because the payload
     * cannot name a target.
     */
    this.onTalk = onTalk || (() => {});

    // The text layer, at the display's real resolution rather than the game's.
    // Optional on purpose: jsdom has no 2D context, and a browser without one
    // should still get a playable game with no labels rather than a crash.
    this.textCanvas = textCanvas || null;
    this.textCtx = this.textCanvas?.getContext('2d') || null;
    this.textDpr = 1;
    this.resizeTextLayer();

    this.buffer = new SnapshotBuffer();
    // Snapshots identify players by their per-room short id, so this is what
    // "which one is me" is answered with. The server sends it in WELCOME.
    this.localShortId = null;
    this.connected = false;

    this.sprite = new Sprite(HERO_SHEET);
    /** @type {Map<number, {name: string, color: string}>} from ROSTER. */
    this.roster = new Map();
    // Speech and emote bubbles. Kept out of React for the same reason
    // snapshots are: they are drawn on the canvas, every frame.
    this.bubbles = new Bubbles();
    // Rising "+1"s. A separate list from bubbles on purpose — see Floaters.
    this.floaters = new Floaters();

    this.camera = new Camera();
    this.tileMap = null; // set by setMap() once WELCOME arrives
    this.portals = [];
    this.signs = [];
    this.zones = [];
    /**
     * Money on the floor, keyed by coin id.
     *
     * DOES NOT GO THROUGH SnapshotBuffer, unlike every moving thing on screen.
     * That buffer is keyed by PLAYER short id and its whole job is lerping x
     * and y between frames — for something that never moves it would only
     * invent motion, and a coin vanishing from a snapshot would be
     * indistinguishable from a coin that walked out of range. Coins arrive as a
     * full list with WELCOME and change by explicit COIN_TAKEN / COIN_SPAWNED,
     * which is the same shape `owners` uses.
     *
     * @type {Map<number, {x: number, y: number}>}
     */
    this.coins = new Map();
    /** zoneId -> { userId, username }. Replaced by setMap, updated by setOwner. */
    this.owners = new Map();
    /** zoneId -> 'hours'|'temporary'|'permanent'. Only the shut ones are in
     *  here; anything absent is open. Replaced by setMap and by setClosures. */
    this.closed = new Map();

    this.frame = null;
    this.lastSent = null;

    /**
     * Where a click asked us to walk, as remaining foot-space waypoints.
     *
     * THE INPUT RULE IS UNTOUCHED BY THIS. The server still only ever receives
     * four held-direction booleans; the path lives here and is turned into
     * exactly those in `sampleInput`. A destination is never sent anywhere.
     */
    this.path = [];
    /** When the character last actually moved. See `followPath`. */
    this.lastProgressAt = 0;
    this.lastFoot = null;
    /**
     * Who a click asked us to go and talk to, as a short id.
     *
     * An INTENTION, not a destination. The walk is an ordinary `path`; this is
     * only the reason for it, and it resolves on being NEAR them rather than on
     * arriving at the tile they were standing on when you clicked — shopkeepers
     * pace, so that tile is usually not where they are by the time you get
     * there.
     */
    this.pendingTalk = null;

    this.tick = this.tick.bind(this);
  }

  start() {
    this.input.start();
    this.frame = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.input.stop();
  }

  /**
   * Called by the socket layer when a snapshot arrives.
   * Buffered rather than applied directly — see SnapshotBuffer.
   */
  applySnapshot(snapshot) {
    this.buffer.push(snapshot, performance.now());
  }

  setLocalShortId(shortId) {
    this.localShortId = shortId;
  }

  /**
   * Somebody's name is now on a building, while we are standing here.
   *
   * A single label rather than a fresh map. The alternative — re-sending
   * WELCOME — would rebuild the tile map, the roster and the camera to change
   * one string, and everyone in the room would see the world blink because a
   * stranger claimed a shop.
   */
  setOwner(zoneId, owner) {
    if (!zoneId) return;
    this.owners.set(zoneId, owner);
  }

  /**
   * Which places are shut, replaced wholesale.
   *
   * REPLACED RATHER THAN MERGED, unlike `setOwner` above — and the difference is
   * the point. Claiming happens to one building at a time, so a delta is the
   * smallest true statement. Nine o'clock happens to every shop at once, so the
   * server sends the whole set; merging it would leave a shop shut all evening
   * because the one message saying it reopened went missing.
   */
  setClosures(closed) {
    this.closed = new Map(Object.entries(closed || {}));
  }

  /**
   * Every coin on the floor, replaced wholesale. From `WELCOME`.
   *
   * MUST BE CALLED AFTER `setMap`, which clears this — coin ids are scoped to a
   * world, so the room you just left had a different coin 7.
   */
  setCoins(list) {
    this.coins = new Map((list || []).map((c) => [c.c, { x: c.x, y: c.y }]));
  }

  /** One coin appeared. Positions are FOOT space — where it sits on the floor. */
  addCoin({ c, x, y } = {}) {
    if (typeof c !== 'number') return;
    this.coins.set(c, { x, y });
  }

  /**
   * One coin was picked up. Drops it, and floats the amount over whoever got it
   * IF they are on screen — a "+1" over somebody you cannot see has nowhere to
   * go, so the silent case is correct rather than a missed frame.
   */
  takeCoin({ c, i, n } = {}) {
    this.coins.delete(c);
    if (typeof i === 'number') this.floaters.add(i, `+${n ?? 1}`);
  }

  /**
   * Walk to a point in the world, pathing around walls.
   *
   * Takes FOOT-space world coordinates, which is what `getLocalFootPosition`
   * returns and what the pathfinder works in — the same convention as routes,
   * spawns and collision.
   */
  walkTo(worldFootPoint) {
    // Deciding to go SOMEWHERE supersedes being on your way to SOMEBODY, and
    // this is the one place that is true of every route — including the
    // minimap, which calls straight in here. `clickAt` sets the errand again
    // afterwards when the destination is a person.
    //
    // Cleared before the early return on purpose: a walk that cannot be
    // computed has still replaced the old intention, and leaving it set would
    // open a conversation minutes later for a click nobody remembers.
    this.pendingTalk = null;

    const from = this.getLocalFootPosition();
    if (!from || !this.tileMap) return;

    this.path = findPath(this.tileMap.wallGrid(), from, worldFootPoint);
    this.lastFoot = from;
    this.lastProgressAt = performance.now();
  }

  /**
   * Give up on wherever we were walking.
   *
   * The intention to talk goes with it. Every way a walk ends — clicking
   * somewhere else, pressing a key, giving up on a stall, arriving — comes
   * through here, so this is the one place that has to forget, and forgetting
   * it here is why pressing W halfway across the plaza does not open a
   * conversation a minute later.
   */
  cancelWalk() {
    this.path = [];
    this.lastFoot = null;
    this.pendingTalk = null;
  }

  /**
   * Whoever is drawn under this world point and is worth talking to, or null.
   *
   * TALKABLE ONLY, from the roster's `k` flag — the same one `drawTalkPrompt`
   * reads. Players are not talkable, so clicking one still just walks, and a
   * character with nothing to say does not silently swallow a click.
   *
   * THE SPRITE'S OWN BOUNDS, because that is the shape a person sees and aims
   * at. Anything more generous starts stealing clicks meant for walking past
   * somebody, which is the more common intent by far.
   */
  talkableAt(worldX, worldY) {
    const half = SPRITE_SIZE / 2;

    return this.buffer.latest().find((p) => {
      if (p.i === this.localShortId) return false;
      if (!this.roster.get(p.i)?.talkable) return false;
      return Math.abs(p.x - worldX) <= half && Math.abs(p.y - worldY) <= half;
    }) || null;
  }

  /**
   * Turn a click on the canvas into a walk, or into a conversation.
   *
   * @param {number} u horizontal position within the canvas, 0..1
   * @param {number} v vertical position within the canvas, 0..1
   *
   * The camera inverse is just `world = screen + camera`, and the click arrives
   * normalised so this never has to know how large the canvas is drawn.
   *
   * CLICKING SOMEBODY MEANS "GO AND TALK TO THEM", which is two things because
   * the protocol allows nothing else: `INTERACT` carries no target, so the
   * client cannot say "that one, over there". It can only close the distance
   * and then ask — which is what click-to-interact means everywhere anyway.
   */
  clickAt(u, v) {
    const world = {
      x: this.camera.x + u * VIEW_WIDTH,
      y: this.camera.y + v * VIEW_HEIGHT,
    };

    const target = this.talkableAt(world.x, world.y);
    if (!target) {
      // Ordinary click: go there. `walkTo` forgets whoever we were on our way
      // to, which is what makes changing your mind mid-approach work.
      this.walkTo(world);
      return;
    }

    // Already close enough — no reason to take a step first.
    if (this.withinTalkRange(target)) {
      this.cancelWalk();
      this.onTalk();
      return;
    }

    this.walkTo({ x: target.x, y: target.y + FOOT_OFFSET_Y });
    this.pendingTalk = target.i;
  }

  /** Is this entity close enough for the server to accept a conversation? */
  withinTalkRange(entity) {
    const me = this.getLocalFootPosition();
    if (!me || !entity) return false;

    // Foot space, matching the server's query. Entity centres would make the
    // range subtly tighter above somebody than below them.
    return Math.hypot(entity.x - me.x, (entity.y + FOOT_OFFSET_Y) - me.y) <= TALK_RANGE;
  }

  /**
   * Have we caught up with whoever we set off to talk to?
   *
   * Called every frame while a walk is running. Resolves on being NEAR them
   * rather than on reaching the tile they occupied when clicked, because
   * shopkeepers pace — arriving at that tile usually means arriving where they
   * used to be.
   *
   * A target who leaves the room stops appearing in snapshots, and the walk
   * ends on its own when the path runs out.
   */
  resolvePendingTalk() {
    if (this.pendingTalk === null) return;

    const target = this.buffer.latest().find((p) => p.i === this.pendingTalk);
    if (!target || !this.withinTalkRange(target)) return;

    this.cancelWalk(); // clears pendingTalk too
    this.onTalk();
  }

  /**
   * Where the local player's FEET are, for recording NPC routes.
   *
   * TWO CONVERSIONS, and getting either wrong is quiet rather than loud.
   *
   * `latestFor` rather than the interpolated position: this is the one entity
   * rendered undelayed (see `render`), so it is also the only one whose
   * position is the server's current truth rather than 100ms of history. A
   * waypoint dropped from an interpolated read would land where you WERE.
   *
   * And `+ FOOT_OFFSET_Y`, because a snapshot carries the entity CENTRE while
   * routes — like spawn points, and like the comparison `npc.js` actually
   * makes — are in foot space. Skip it and every recorded route sits 7 units
   * high: invisible on open floor, and the difference between walkable and not
   * in a doorway. The conversion lives here so there is one definition of
   * "where am I standing" rather than one per caller.
   *
   * @returns {{x: number, y: number} | null} null before the first snapshot
   */
  getLocalFootPosition() {
    if (!this.localShortId) return null;

    const local = this.buffer.latestFor(this.localShortId);
    if (!local) return null;

    return { x: local.x, y: local.y + FOOT_OFFSET_Y };
  }

  /** Hand the keyboard to the game, or release it so chat can be typed in. */
  setInputEnabled(value) {
    this.input.setEnabled(value);
  }

  /**
   * Size the text layer's backing store to the display.
   *
   * Its CSS box is the same 960x540 as the art canvas, but the backing store is
   * that times the device pixel ratio — which is what actually makes glyphs
   * sharp. The transform is set once so every later call works in CSS pixels
   * and never has to think about DPR again.
   */
  resizeTextLayer() {
    if (!this.textCtx) return;

    this.textDpr = Math.min(window.devicePixelRatio || 1, MAX_TEXT_DPR);
    this.textCanvas.width = VIEW_WIDTH * VIEW_SCALE * this.textDpr;
    this.textCanvas.height = VIEW_HEIGHT * VIEW_SCALE * this.textDpr;
    this.textCtx.setTransform(this.textDpr, 0, 0, this.textDpr, 0, 0);
  }

  /**
   * Run a drawing callback on the text layer, in CSS pixels.
   *
   * Callers hold world coordinates, so the callback is handed `VIEW_SCALE` to
   * convert with — positions AND font sizes. A 6-unit label becomes an 18px
   * glyph: the same apparent size it has today, minus the blur.
   *
   * The context is NOT passed in; callers reach for `this.textCtx`. Handing it
   * over as a parameter and then setting `fillStyle` on it trips
   * `no-param-reassign`, and this is tidier than the local-alias dodge used in
   * the server's world.js.
   *
   * A no-op when there is no text layer, which is what keeps the game running
   * under jsdom.
   */
  onTextLayer(draw) {
    if (!this.textCtx) return;

    this.textCtx.save();
    draw(VIEW_SCALE);
    this.textCtx.restore();
  }

  /** A CHAT payload from the server: `{ i, t }`. System replies have no `i`. */
  applyChat({ i, t } = {}) {
    if (typeof i === 'number') this.bubbles.say(i, t);
  }

  /** An EMOTE payload from the server: `{ i, e }`. */
  applyEmote({ i, e } = {}) {
    if (typeof i === 'number') this.bubbles.emote(i, e);
  }

  /**
   * Adopt a ROSTER payload: what the short ids in snapshots mean.
   *
   * Replaced wholesale rather than merged. The server sends the complete room
   * every time, and merging would leave departed players in the map forever —
   * whose ids are then reissued to somebody else.
   */
  setRoster(payload) {
    this.roster = new Map(
      (payload?.players || []).map((p) => [p.i, {
        name: p.n,
        color: p.c,
        appearance: p.a,
        talkable: Boolean(p.k),
        // Unpublished. Only ever present for admins — the server filters these
        // entries out of everyone else's roster entirely, so this flag is a
        // label to draw, never a decision about what to show.
        draft: Boolean(p.d),
      }]),
    );
  }

  /**
   * Adopt the map the server sent with WELCOME.
   *
   * The tileset image is resolved client-side under /tilesets/ — the server
   * sends only a basename, since it owns the map data but not the art.
   */
  setMap(map, owners = {}, closed = {}) {
    if (!map) return;
    this.tileMap = new TileMap(map, `${PUBLIC}/tilesets/${map.tileset.image}`);
    this.camera.setMap(map);

    // Portals and signs travel with the map. Portals are drawn where the
    // server's trigger actually is — a doorway you can see but walk through
    // is worse than no doorway. Signs are purely local: reading one changes
    // no game state, so the server never hears about it.
    this.portals = map.portals || [];
    this.signs = map.signs || [];
    // Buildings, as names over walls that are already in the tile layer.
    // Nothing is derived from these — collision never sees them.
    this.zones = map.zones || [];
    // Who owns what, arriving beside the map rather than inside it: the map is
    // built once per room and shared, while ownership changes while that room
    // is alive. Keyed by the zone's stable id, so it survives the zone list
    // being reordered in the editor.
    this.owners = new Map(Object.entries(owners || {}));
    // Which of them are shut, arriving beside the map for the same reason
    // ownership does — and read at join time, so somebody arriving after
    // closing sees it on their first frame rather than 30 seconds later.
    this.closed = new Map(Object.entries(closed || {}));

    // Drop buffered snapshots from the previous map. Without this, the first
    // frames after a switch interpolate between a position in the OLD world
    // and one in the new — every player visibly slides across the map on
    // arrival, which looks like a physics bug rather than a stale buffer.
    this.buffer.clear();
    // Short ids are scoped to a room, so the old room's roster now decodes to
    // the wrong people. The server sends a fresh one immediately after WELCOME.
    this.roster.clear();
    // Same reason: a bubble keyed by short id would reappear over whoever
    // inherits that id in the new room.
    this.bubbles.clear();
    // And a "+1" would rise over a stranger.
    this.floaters.clear();
    // Coin ids are scoped to a world too, so the old room's coin 7 is a
    // different coin. The caller sends the new list immediately after this —
    // `setCoins` must run AFTER `setMap`, never before.
    this.coins.clear();
  }

  setConnected(value) {
    this.connected = value;
  }

  /**
   * One rendered frame.
   *
   * Input is sampled and drawing happens here, but simulation does not — the
   * server owns that. This loop's only jobs are "tell the server what I'm
   * holding" and "draw whatever the server last said."
   */
  tick() {
    this.sampleInput();
    this.render();
    this.frame = requestAnimationFrame(this.tick);
  }

  sampleInput() {
    const held = this.input.snapshot();

    // THE KEYBOARD ALWAYS WINS. Touching a movement key abandons the click,
    // which is what stops the two fighting over the same character.
    const pressing = held.up || held.down || held.left || held.right;
    if (pressing && this.path.length > 0) this.cancelWalk();

    // BEFORE stepping, so the last stride of an approach is not taken after we
    // are already close enough — and so a target who walks TO us is met without
    // either of us finishing a route.
    this.resolvePendingTalk();

    const current = this.path.length > 0 ? this.followPath() : held;
    const serialized = JSON.stringify(current);

    // Only emit when the held set actually changes. At 60fps an unconditional
    // emit would be 60 packets/sec/player of mostly identical data.
    if (serialized !== this.lastSent) {
      this.lastSent = serialized;
      this.onInputChange(current);
    }
  }

  /**
   * One step along a clicked route, as held keys.
   *
   * Gives up if the character stops making progress — the server is the
   * authority on collision, and this module's walkability model is only a
   * model. Better to stop than to shove into a wall forever, which is the same
   * conclusion npc.js reached for scripted routes.
   */
  followPath(now = performance.now()) {
    const from = this.getLocalFootPosition();
    if (!from) return this.input.snapshot();

    // `reached` is the exact negation of "steer presses something", so a
    // waypoint we are not moving toward is always consumed here rather than
    // leaving the character standing on it pressing nothing.
    while (this.path.length > 0 && reached(from, this.path[0])) this.path.shift();
    if (this.path.length === 0) {
      this.cancelWalk();
      return this.input.snapshot();
    }

    /**
     * Give up if we have wanted to move for a while and have not.
     *
     * MEASURED IN TIME, NOT FRAMES, and that distinction is the whole fix. The
     * previous version counted frames where the position was unchanged — but
     * this runs at 60fps while snapshots arrive at 20Hz, so the position is
     * identical on two of every three frames DURING A HEALTHY WALK. The counter
     * peaked at 2 against a threshold of 90 and could never fire: it read as a
     * safety net and was dead code.
     *
     * Wall-clock against real movement is correct whatever the frame or
     * snapshot rate.
     */
    if (!this.lastFoot || Math.hypot(from.x - this.lastFoot.x, from.y - this.lastFoot.y) > 0.5) {
      this.lastFoot = from;
      this.lastProgressAt = now;
    } else if (now - this.lastProgressAt > STALL_GIVE_UP_MS) {
      // Shoved against geometry the client's model thought was clear. The
      // server is the authority, so stop rather than push forever.
      this.cancelWalk();
      return this.input.snapshot();
    }

    return steer(from, this.path[0]);
  }

  render() {
    const { ctx, camera } = this;
    const nowMs = performance.now();

    // The camera follows the local player. Until one exists — before WELCOME,
    // or on a snapshot that predates it — it holds its last position rather
    // than snapping to the origin.
    //
    // The local player and the camera use the LATEST position, not the
    // interpolated one. Delaying your own character by 100ms on top of the
    // input round trip would make the controls feel materially worse, and a
    // camera lagging its target is far more noticeable than a remote player
    // trailing slightly.
    const local = this.localShortId ? this.buffer.latestFor(this.localShortId) : null;
    camera.follow(local);

    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);

    // The text layer is transparent and redrawn from scratch every frame — the
    // camera moves, so every label moves, and there is nothing to cache.
    if (this.textCtx) {
      this.textCtx.clearRect(0, 0, VIEW_WIDTH * VIEW_SCALE, VIEW_HEIGHT * VIEW_SCALE);
    }

    // Layer order, matching the reference engine's lower/upper sandwich:
    // ground, then walls, then entities, then anything that should occlude
    // them (rooftops, tree canopy).
    if (this.tileMap) {
      this.tileMap.draw(ctx, 'floor', camera);
      this.drawPortals(nowMs);
      this.tileMap.draw(ctx, 'walls', camera);
      // AFTER the walls, not beside `drawPortals` between floor and walls. A
      // portal is painted ON the ground; a coin is an object standing on it, so
      // it should be hidden by a rooftop in the `above` layer and covered by a
      // player who walks onto it — which reads correctly, since walking onto
      // one is the instant before it is taken.
      this.drawCoins(nowMs);
    }

    // Remote players are drawn INTERPOLATED_DELAY_MS in the past, blended
    // between snapshots — that is what removes the 20Hz stepping. The local
    // player is swapped back to its latest position afterwards.
    const rendered = this.buffer.interpolated(nowMs).map((p) => (
      p.i === this.localShortId && local ? local : p
    ));

    // Sort by y so players lower on screen draw in front — the same rule the
    // reference engine adds in video 6. Costs nothing at these counts.
    const ordered = rendered.sort((a, b) => a.y - b.y);

    // Swept once here rather than per player, then grouped so the loop below is
    // a lookup. A floater for somebody absent from this frame simply never gets
    // drawn, which is the wanted behaviour rather than a dropped case.
    const floaters = new Map();
    this.floaters.active().forEach((f) => {
      const list = floaters.get(f.shortId);
      if (list) list.push(f);
      else floaters.set(f.shortId, [f]);
    });

    ordered.forEach((player) => {
      const isLocal = player.i === this.localShortId;
      const sx = camera.toScreenX(player.x);
      const sy = camera.toScreenY(player.y);

      // Skip anything outside the viewport. The server already culls by
      // interest, but it adds a margin so players fade in rather than pop, so
      // a few arrivals are legitimately off-screen on the frame they appear.
      if (sx < -SPRITE_MARGIN || sy < -SPRITE_MARGIN
        || sx > VIEW_WIDTH + SPRITE_MARGIN || sy > VIEW_HEIGHT + SPRITE_MARGIN) {
        return;
      }

      // Names and colours come from the roster, not the snapshot — they are
      // immutable, so sending them 20 times a second was the bulk of the old
      // payload. A miss means a ROSTER is in flight; draw them anyway rather
      // than leaving a hole in the world for a frame.
      const identity = this.roster.get(player.i);

      // The sprite carries identity now, but a tinted shadow still helps pick
      // someone out of a crowd at a glance, so it follows their shirt.
      const marker = identity?.appearance?.shirt || identity?.color || UNKNOWN_COLOR;
      this.drawShadow(sx, sy, marker, isLocal);

      const drew = this.sprite.draw(ctx, {
        source: variantFor(identity?.appearance),
        x: sx,
        y: sy,
        facing: FACINGS[player.f] || 'down',
        moving: Boolean(player.m),
        nowMs,
        phase: phaseFor(player.i),
      });

      // Fallback: a missing or still-loading sheet renders the original
      // square rather than nothing. An absent asset must never blank the game.
      if (!drew) {
        const half = PLAYER_SIZE / 2;
        ctx.fillStyle = identity?.appearance?.shirt || identity?.color || UNKNOWN_COLOR;
        ctx.fillRect(sx - half, sy - half, PLAYER_SIZE, PLAYER_SIZE);
      }

      if (identity) this.drawNameplate(identity.name, sx, sy, identity.draft);

      const bubble = this.bubbles.for(player.i);
      if (bubble) this.drawBubble(bubble, sx, sy);

      // Inside this loop because it is the only place a picker's screen
      // position is known — a floater is anchored to a person, not to a spot.
      const mine = floaters.get(player.i);
      if (mine) mine.forEach((f) => this.drawFloater(f, sx, sy));
    });

    // Anyone who walked out of range keeps their bubble otherwise — invisible
    // but retained, which leaks in a room players cycle through.
    this.bubbles.prune(new Set(ordered.map((p) => p.i)));

    if (this.tileMap) this.tileMap.draw(ctx, 'above', camera);

    // A building's name sits under the sign text but over the rooftops, so a
    // shop is identifiable from outside without hiding what a sign says.
    this.drawZoneLabels();

    // Signs read last so their text sits over everything, including the
    // rooftops layer — a label half-hidden behind a canopy is worse than one
    // that floats.
    this.drawSigns(local);
    // One prompt at a time, and talking wins — mirroring the server, which only
    // considers claiming when `talkableNear` finds nobody. Two prompts at once
    // would also be two instructions for one key.
    if (!this.drawTalkPrompt(local)) this.drawClaimPrompt(local);
    this.drawWalkTarget();

    if (!this.connected) {
      this.drawBanner('Connecting…');
    } else if (this.buffer.isEmpty) {
      this.drawBanner('Waiting for the first snapshot…');
    }
  }

  /**
   * Coloured ellipse under the feet.
   *
   * Once the only identity marker, back when every player shared one sheet.
   * The character now carries identity itself, so this is a secondary cue —
   * tinted to match the shirt, which is what makes someone findable in a crowd
   * at a glance without reading nameplates.
   */
  drawShadow(sx, sy, color, isLocal) {
    const { ctx } = this;
    const half = PLAYER_SIZE / 2;

    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(sx, sy + half - 1, half * 0.8, half * 0.32, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    if (isLocal) {
      ctx.save();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(sx, sy + half - 1, half * 0.9, half * 0.4, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  /**
   * Doorways to other maps.
   *
   * Drawn between the floor and the walls, so a portal reads as painted on the
   * ground and anything solid still occludes it. The pulse is the only moving
   * thing on an otherwise static floor, which is what makes a doorway findable
   * without a marker cluttering the HUD.
   */
  drawPortals(nowMs) {
    if (this.portals.length === 0) return;
    const { ctx, camera } = this;

    // 2.4s cycle. Slow enough to read as a glow rather than a flicker.
    const pulse = 0.35 + 0.25 * Math.sin(nowMs / 380);

    this.portals.forEach((p) => {
      const sx = camera.toScreenX(p.x);
      const sy = camera.toScreenY(p.y);

      // Cheap reject — most portals are off-screen on a four-screen map.
      if (sx > VIEW_WIDTH || sy > VIEW_HEIGHT || sx + p.width < 0 || sy + p.height < 0) return;

      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = '#4CAFD4';
      ctx.fillRect(sx, sy, p.width, p.height);
      ctx.globalAlpha = Math.min(1, pulse + 0.4);
      ctx.strokeStyle = '#8FE3FF';
      ctx.lineWidth = 1;
      ctx.strokeRect(sx + 0.5, sy + 0.5, p.width - 1, p.height - 1);
      ctx.restore();

      if (p.label) {
        this.onTextLayer((s) => {
          const t = this.textCtx;
          t.fillStyle = 'rgba(143, 227, 255, 0.9)';
          t.font = `${6 * s}px system-ui, sans-serif`;
          t.textAlign = 'center';
          t.fillText(p.label, (sx + p.width / 2) * s, (sy - 3) * s);
        });
      }
    });
  }

  /**
   * Money on the floor.
   *
   * The shape itself is `coinSprite.js` — pure, so the whole-pixel rule that
   * keeps it crisp under the 3x upscale can be tested. Everything here is the
   * camera and the loop.
   */
  drawCoins(nowMs) {
    if (this.coins.size === 0) return;
    const { ctx, camera } = this;

    this.coins.forEach((coin, id) => {
      const sx = Math.round(camera.toScreenX(coin.x));
      /**
       * NO `FOOT_OFFSET_Y` HERE.
       *
       * A coin's y IS its foot position — it is lying on the ground, not
       * standing on it. Players are the exception, not the rule: their snapshot
       * y is a body centre with the feet below. Adding the offset by analogy
       * would draw every coin seven pixels north of where it is collected, and
       * the mismatch would look like laggy pickup rather than a drawing bug.
       */
      const sy = Math.round(camera.toScreenY(coin.y));

      // Cheap reject, like drawPortals. Free at these counts; the point is that
      // it stays free on a map carrying sixty.
      if (sx < -COIN_MARGIN || sy < -COIN_MARGIN
        || sx > VIEW_WIDTH + COIN_MARGIN || sy > VIEW_HEIGHT + COIN_MARGIN) {
        return;
      }

      coinRects(nowMs, id).forEach(([dx, dy, w, h, color]) => {
        ctx.fillStyle = color;
        ctx.fillRect(sx + dx, sy + dy, w, h);
      });
    });
  }

  /**
   * A "+1" rising off somebody's head.
   *
   * On the TEXT layer rather than the art canvas, like every other string in
   * the engine: at 320x180 a 7px font is unreadable, and the text layer exists
   * at device resolution for exactly this.
   */
  drawFloater({ text, dy, alpha }, sx, sy) {
    this.onTextLayer((s) => {
      const t = this.textCtx;
      // Safe to set because onTextLayer save/restores around this callback.
      t.globalAlpha = alpha;
      t.fillStyle = '#F5D97A'; // the prompt gold — "this one is yours"
      t.font = `${7 * s}px system-ui, sans-serif`;
      t.textAlign = 'center';
      t.fillText(text, sx * s, (sy - PLAYER_SIZE - 2 + dy) * s);
    });
  }

  /**
   * Sign posts, and their text when the local player is close enough to read.
   *
   * Entirely client-side. A sign changes nothing, so involving the server would
   * be a round trip to be told what the client already has in the map payload.
   */
  /**
   * "Press E to talk" over the nearest NPC that has something to say.
   *
   * Drawn ONLY for entities the roster marks talkable, which is the whole point
   * of that flag: a prompt that always appears tells you nothing, while one
   * that appears selectively is a reliable signal there is something there.
   *
   * `TALK_RANGE` is shared with the server, which is what enforces it. If this
   * drew at a different distance you would get a prompt that does nothing when
   * pressed — the client renders the affordance, the server decides the rule.
   *
   * Compared in FOOT space, matching the server's query. Entity centres would
   * make the range subtly tighter above an NPC than below it.
   */
  drawTalkPrompt(local) {
    if (!local) return null;

    const localFootY = local.y + FOOT_OFFSET_Y;
    let nearest = null;
    let bestDistance = Infinity;

    this.buffer.latest().forEach((p) => {
      if (p.i === this.localShortId) return;
      if (!this.roster.get(p.i)?.talkable) return;

      const distance = Math.hypot(p.x - local.x, (p.y + FOOT_OFFSET_Y) - localFootY);
      if (distance > TALK_RANGE || distance >= bestDistance) return;

      nearest = p;
      bestDistance = distance;
    });

    if (!nearest) return null;

    const sx = this.camera.toScreenX(nearest.x);
    const sy = this.camera.toScreenY(nearest.y);

    this.onTextLayer((scale) => {
      const t = this.textCtx;
      t.font = `${6 * scale}px system-ui, sans-serif`;
      t.textAlign = 'center';
      t.fillStyle = '#F5D97A';
      t.fillText('Press E to talk', sx * scale, (sy - 22) * scale);
    });

    return nearest.i;
  }

  /**
   * "Press E to claim this place", when standing somewhere nobody owns.
   *
   * WITHOUT THIS THE FEATURE DOES NOT EXIST. You could stand dead centre in an
   * unclaimed shop and nothing would suggest a key does anything — the same
   * point the talk prompt above is written around: a prompt that appears
   * selectively is a reliable signal there is something there, and no prompt at
   * all is a mechanic nobody finds.
   *
   * ONLY WHEN NOBODY IS TALKABLE, which is not a style choice. The server runs
   * `talkableNear` first and only claims when it finds nobody, so a shopkeeper
   * standing in their own shop is somebody you talk to. A prompt that
   * disagreed would tell you to claim and then open a conversation.
   *
   * OVER THE PLAYER, not over the building. A zone can be far wider than the
   * viewport, so its centre is often off screen — and this is a hint about what
   * YOU can do rather than a label on the place.
   */
  drawClaimPrompt(local) {
    if (!local) return null;

    const zone = zoneAt({ zones: this.zones }, local.x, local.y + FOOT_OFFSET_Y);
    if (!zone || this.owners.has(zone.id)) return null;
    // Businesses, landmarks and parks are not somewhere to live. Checked here
    // as well as on the server so the prompt is never an instruction that gets
    // refused — the same reason `TALK_RANGE` is mirrored.
    if (!CLAIMABLE_KINDS.includes(zone.kind)) return null;

    const sx = this.camera.toScreenX(local.x);
    const sy = this.camera.toScreenY(local.y);

    this.onTextLayer((scale) => {
      const t = this.textCtx;
      t.font = `${6 * scale}px system-ui, sans-serif`;
      t.textAlign = 'center';
      t.fillStyle = '#F5D97A';
      t.fillText('Press E to claim this place', sx * scale, (sy - 22) * scale);
    });

    return zone.id;
  }

  /**
   * A marker on where a click sent us.
   *
   * Cheap, and it is the only feedback that a click registered at all — without
   * it a click that resolved to "unreachable" is indistinguishable from one the
   * page never received.
   */
  drawWalkTarget() {
    if (this.path.length === 0) return;

    const target = this.path[this.path.length - 1];
    const sx = this.camera.toScreenX(target.x);
    const sy = this.camera.toScreenY(target.y);
    const { ctx } = this;

    ctx.strokeStyle = '#F5D97A';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sx - 3, sy - 3);
    ctx.lineTo(sx + 3, sy + 3);
    ctx.moveTo(sx + 3, sy - 3);
    ctx.lineTo(sx - 3, sy + 3);
    ctx.stroke();
  }

  /**
   * A building's name, centred over it.
   *
   * NO PROXIMITY CHECK, unlike signs. A sign is something you walk up to and
   * read; a building's name is how you know which one to walk up to at all.
   * Hiding it until you were standing inside would defeat the point.
   *
   * Culled to the viewport, because a town with forty buildings would
   * otherwise measure and draw forty labels a frame to show the six on screen.
   *
   * ON THE TEXT LAYER, like every other string in here. It used to draw straight
   * onto the world canvas, which is 320x180 before the viewport scales it about
   * six times — so a 5px glyph arrived as a ~30px nearest-neighbour smear while
   * the nameplate a few pixels below it stayed sharp. That was the only
   * `ctx.fillText` in the engine, and the odd one out was the broken one.
   *
   * It also asked for `Press Start 2P`, a font referenced nowhere else and never
   * loaded — no @font-face, no link, no file — so it had always been silently
   * falling back to monospace. Gone; this uses the same stack as the rest.
   */
  drawZoneLabels() {
    if (this.zones.length === 0) return;

    const { camera } = this;

    this.onTextLayer((s) => {
      const t = this.textCtx;
      t.font = `${7 * s}px system-ui, sans-serif`;
      t.textAlign = 'center';
      t.textBaseline = 'middle';

      this.zones.forEach((zone) => {
        // An owned building is worth naming even if the world author never
        // gave it one — "Jason's place" beats an unlabelled box.
        const label = zoneLabelFor(zone, this.owners.get(zone.id), this.closed.get(zone.id));
        if (!label) return;

        const cx = Math.round(zone.x + zone.width / 2 - camera.x);
        const cy = Math.round(zone.y + zone.height / 2 - camera.y);
        if (cx < -60 || cy < -20 || cx > VIEW_WIDTH + 60 || cy > VIEW_HEIGHT + 20) return;

        // Measured on the text context, because that is where the glyphs are
        // now — measuring on the world canvas would size the plate for a font
        // nothing is drawn in.
        const { width } = t.measureText(label);
        t.fillStyle = 'rgba(2,6,23,0.6)';
        t.fillRect(cx * s - width / 2 - 3 * s, cy * s - 6 * s, width + 6 * s, 12 * s);

        // Gold against the nameplate's white: a place should not read as a
        // person, and that was the one thing the old version got right. Grey
        // when shut — the words say which kind of closed, but at a 7px glyph
        // across a street the colour is most of what a player actually reads.
        t.fillStyle = this.closed.has(zone.id) ? '#8b93a7' : '#f5d97a';
        t.fillText(label, cx * s, cy * s);
      });
    });
  }

  drawSigns(local) {
    if (this.signs.length === 0) return;
    const { ctx, camera } = this;

    this.signs.forEach((sign) => {
      const sx = camera.toScreenX(sign.x);
      const sy = camera.toScreenY(sign.y);
      if (sx < -16 || sy < -16 || sx > VIEW_WIDTH + 16 || sy > VIEW_HEIGHT + 16) return;

      // The post itself, so a sign is visible from across the map even when
      // its text is not.
      ctx.fillStyle = '#6B4F2A';
      ctx.fillRect(sx - 1, sy - 4, 2, 6);
      ctx.fillStyle = '#C8A26A';
      ctx.fillRect(sx - 5, sy - 9, 10, 6);

      if (!local) return;
      const near = Math.abs(local.x - sign.x) < SIGN_READ_RANGE
        && Math.abs(local.y - sign.y) < SIGN_READ_RANGE;
      if (!near) return;

      const lines = wrap(sign.text, 30, 2);

      // Panel and text both go on the text layer: the box is sized from
      // measureText, so measuring and drawing have to happen in the same space
      // or the background will not fit the words it is behind.
      this.onTextLayer((s) => {
        const t = this.textCtx;
        t.font = `${6 * s}px system-ui, sans-serif`;
        t.textAlign = 'center';

        const widest = lines.reduce((w, l) => Math.max(w, t.measureText(l).width), 0);
        const boxW = Math.ceil(widest) + 6 * s;
        const boxH = lines.length * 8 * s + 4 * s;
        const top = (sy - 12) * s - boxH;

        t.fillStyle = 'rgba(13, 27, 42, 0.9)';
        t.fillRect(sx * s - boxW / 2, top, boxW, boxH);
        t.fillStyle = '#C8A26A';
        lines.forEach((line, i) => t.fillText(line, sx * s, top + 2 * s + (i + 1) * 8 * s - 2 * s));
      });
    });
  }

  /**
   * A speech bubble above the nameplate.
   *
   * Panel and text both live on the text layer — the box is sized from
   * measureText, so measuring and drawing must happen in the same space or the
   * background will not fit the words it sits behind.
   *
   * Panel first, then text, because canvas has no z-index: whatever is drawn
   * last wins, and a background painted after its text would erase it.
   */
  drawBubble(bubble, sx, sy) {
    const { lines, emote } = bubble;

    this.onTextLayer((s) => {
      const t = this.textCtx;
      const fontPx = (emote ? 8 : 6) * s;
      const lineHeight = fontPx + 2 * s;
      t.font = `${fontPx}px system-ui, sans-serif`;
      t.textAlign = 'center';

      const widest = lines.reduce((w, l) => Math.max(w, t.measureText(l).width), 0);
      const padX = 3 * s;
      const padY = 2 * s;
      const boxW = Math.ceil(widest) + padX * 2;
      const boxH = lines.length * lineHeight + padY * 2;

      // Sits above the nameplate, which itself sits at sy - PLAYER_SIZE.
      const boxBottom = (sy - PLAYER_SIZE - 7) * s;
      const boxTop = boxBottom - boxH;

      t.fillStyle = 'rgba(13, 27, 42, 0.85)';
      t.fillRect(sx * s - boxW / 2, boxTop, boxW, boxH);

      // A small tail, so a bubble reads as belonging to the head under it
      // rather than hovering independently.
      t.fillRect(sx * s - s, boxBottom, 2 * s, 2 * s);

      t.fillStyle = emote ? '#D4A24C' : '#F2F5F7';
      lines.forEach((line, i) => {
        t.fillText(line, sx * s, boxTop + padY + (i + 1) * lineHeight - 2 * s);
      });
    });
  }

  /**
   * @param {boolean} [isDraft] mark them as not yet visible to players.
   *   Only ever true for an admin — a draft's roster entry never reaches
   *   anyone else — so this is a label, not a permission check.
   */
  drawNameplate(name, sx, sy, isDraft = false) {
    this.onTextLayer((s) => {
      const t = this.textCtx;
      t.fillStyle = 'rgba(255,255,255,0.85)';
      t.font = `${6 * s}px system-ui, sans-serif`;
      t.textAlign = 'center';
      t.fillText(name, sx * s, (sy - PLAYER_SIZE) * s);

      // Above the name rather than beside it, so a long name cannot push the
      // tag off-centre and leave you guessing which character it belongs to.
      if (isDraft) {
        t.fillStyle = 'rgba(245,158,11,0.95)'; // amber, matching the admin badge
        t.font = `${5 * s}px system-ui, sans-serif`;
        t.fillText('draft', sx * s, (sy - PLAYER_SIZE - 7) * s);
      }
    });
  }

  drawBanner(text) {
    this.onTextLayer((s) => {
      const t = this.textCtx;
      t.fillStyle = '#D4A24C';
      t.font = `${8 * s}px system-ui, sans-serif`;
      t.textAlign = 'center';
      t.fillText(text, (VIEW_WIDTH / 2) * s, (VIEW_HEIGHT / 2) * s);
    });
  }
}
